import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { useUpgradeStore } from '@/stores/upgrade.store'
import type { ApiResponse } from '@/types/api'
import type { BillingStatus } from '@/types/models'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Plan table data
// ---------------------------------------------------------------------------

const PLANS = [
  {
    name: 'Free',
    monthlyPrice: '₹0',
    yearlyPrice: '₹0',
    applications: '3',
    tier: 'FREE',
  },
  {
    name: 'Pro',
    monthlyPrice: '₹1,500/mo',
    yearlyPrice: '₹10,000/yr',
    applications: '8',
    tier: 'PRO',
  },
  {
    name: 'Max',
    monthlyPrice: '₹3,000/mo',
    yearlyPrice: '₹20,000/yr',
    applications: '20',
    tier: 'MAX',
  },
]

const TIER_BADGE: Record<string, string> = {
  FREE: 'bg-muted text-muted-foreground',
  PRO: 'bg-brand-purple/10 text-brand-purple',
  MAX: 'bg-brand-green/10 text-brand-green',
}

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------

/**
 * BillingPage — displays current plan and plan comparison.
 *
 * FREE tier: shows upgrade buttons that open UpgradeModal.
 * PRO/MAX ACTIVE: shows renewal date and cancel link.
 */
export default function BillingPage() {
  const { currentWorkspaceId } = useWorkspaceStore()
  const openUpgrade = useUpgradeStore((s) => s.open)

  const { data, isLoading } = useQuery<ApiResponse<BillingStatus>>({
    queryKey: ['billing-status', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<BillingStatus>>('/v1/billing/status'),
    enabled: !!currentWorkspaceId,
  })
  const billing = data?.data

  const isPaid = billing?.tier === 'PRO' || billing?.tier === 'MAX'
  const isActive = billing?.status === 'ACTIVE'

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold text-foreground mb-8">Billing</h1>

      {/* Current plan card */}
      {isLoading && (
        <div className="text-sm text-muted-foreground mb-6">Loading billing info...</div>
      )}

      {billing && (
        <div className="mb-8 rounded-xl border bg-card p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xl font-bold text-foreground">{billing.tier}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    TIER_BADGE[billing.tier],
                  )}
                >
                  {billing.tier}
                </span>
              </div>
            </div>
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                billing.status === 'ACTIVE'
                  ? 'bg-brand-green/10 text-brand-green'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {billing.status}
            </span>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
            <div className="flex justify-between rounded-md bg-muted/30 px-3 py-2">
              <span className="text-muted-foreground">Applications Limit</span>
              <span className="font-medium">{billing.limit === -1 ? 'Unlimited' : billing.limit}</span>
            </div>
            <div className="flex justify-between rounded-md bg-muted/30 px-3 py-2">
              <span className="text-muted-foreground">Used</span>
              <span className="font-medium">{billing.applicationsUsed}</span>
            </div>
          </div>

          {isPaid && isActive && billing.subscriptionEndsAt && (
            <p className="mt-4 text-sm text-muted-foreground">
              Your plan renews on{' '}
              <strong>
                {new Date(billing.subscriptionEndsAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </strong>
            </p>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-3">
            {!isPaid && (
              <>
                <button
                  type="button"
                  onClick={openUpgrade}
                  className="rounded-md bg-brand-purple px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  Upgrade to Pro
                </button>
                <button
                  type="button"
                  onClick={openUpgrade}
                  className="rounded-md bg-brand-green px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  Upgrade to Max
                </button>
              </>
            )}
            {isPaid && isActive && (
              <a
                href="mailto:support@gradnomad.com?subject=Cancel%20Subscription"
                className="rounded-md border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
              >
                Cancel Subscription
              </a>
            )}
          </div>
        </div>
      )}

      {/* Plan comparison table */}
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Plan Comparison</h2>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-4 py-3 text-left font-semibold text-foreground">Plan</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">Monthly</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">Yearly</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">Applications</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {PLANS.map((plan) => {
                const isCurrent = billing?.tier === plan.tier
                return (
                  <tr
                    key={plan.tier}
                    className={cn(
                      'border-b last:border-0',
                      isCurrent && 'bg-brand-blue/5',
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{plan.name}</span>
                        {isCurrent && (
                          <span className="flex items-center gap-1 rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue">
                            <Check className="h-3 w-3" />
                            Current
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground">{plan.monthlyPrice}</td>
                    <td className="px-4 py-3 text-foreground">{plan.yearlyPrice}</td>
                    <td className="px-4 py-3 text-foreground">{plan.applications}</td>
                    <td className="px-4 py-3">
                      {!isCurrent && plan.tier !== 'FREE' && (
                        <button
                          type="button"
                          onClick={openUpgrade}
                          className={cn(
                            'rounded-md px-3 py-1 text-xs font-semibold text-white hover:opacity-90',
                            plan.tier === 'PRO' ? 'bg-brand-purple' : 'bg-brand-green',
                          )}
                        >
                          Upgrade
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
